import { useEffect, useMemo, useState } from 'react';

import { FormValidator, useForm, UseFormActions } from '@/hooks/useForm';
import { siteConfig } from '@/lib/i18n/en';
import { TranslationsTypes, useTranslation } from '@/lib/i18n/i18n.client';
import { getIntlText } from '@/lib/i18n/i18n.server';
import { cn } from '@/lib/utils';

import Button from '@/components/atoms/Button';
import { Checkbox } from '@/components/atoms/Checkbox';
import { Input } from '@/components/atoms/Input';
import LinkButton from '@/components/atoms/LinkButton';
import { Text } from '@/components/atoms/Text';
import { TelephoneInput } from '@/components/molecules/TelephoneInput';
import { SvgIcon } from '@/components/SvgIcon';

import { EMAIL_REGEX } from '@/utils/validation';

import { countries } from '../constants/countries';
import {
  COVER_LETTER_MAX_LENGTH,
  MAX_RESUME_FILE_SIZE_BYTES,
  MAX_RESUME_FILE_SIZE_MB,
  SUPPORTED_FILE_TYPES,
} from '../constants/form';
import { formatList } from '../helpers';
import { FileUploadField } from './FileUploadField';

import iconCheckSrc from '~/icons/check.svg';
import iconProfileLinkSrc from '~/svg/IconProfileLink.svg';
const iconSpinnerSrc = '/svg/IconSpinner.svg';

export const DATA_PROTECTION_URL = '/legal/data-protection';
const inputBorderClasses = 'rounded border border-[#959595] bg-white';

export interface ApplicationFormSchema {
  fullName: string;
  email: string;
  countryCode: string;
  phoneNumber: string;
  linkedInProfile?: string;
  resume?: File;
  consent: boolean;
  information?: string;
}

const getValidator = (
  texts: TranslationsTypes
): FormValidator<ApplicationFormSchema> => ({
  fullName: (value) => {
    if (typeof value !== 'string') return;
    if (!value) return texts.job_application.error.full_name;
    if (value.length > 50)
      return texts.job_application.error.max_of_50_characters_allowed;
  },
  email: (value) => {
    if (typeof value !== 'string') return;
    if (!value) return texts.job_application.error.email;
    if (!EMAIL_REGEX.test(value))
      return texts.job_application.error.invalid_email;
  },
  resume: (value) => {
    if (!value) return texts.job_application.error.resume_required;
    if (!(value instanceof File))
      return texts.job_application.error.resume_must_be_a_file;
    if (
      !SUPPORTED_FILE_TYPES.some((type) =>
        value.name.toLowerCase().endsWith(type)
      )
    )
      return getIntlText(texts.job_application.error.file_type_not_supported, {
        fileTypes: formatList(SUPPORTED_FILE_TYPES, 'or'),
      });
    if (value.size > MAX_RESUME_FILE_SIZE_BYTES)
      return getIntlText(
        texts.job_application.error.choose_a_file_smaller_than_mb,
        {
          maxFileSize: String(MAX_RESUME_FILE_SIZE_MB),
        }
      );
  },
  countryCode: (value) => {
    if (typeof value !== 'string') return;
    if (
      value &&
      !/^\+\d{1,3}$/.test(value) &&
      countries.find((country) => country.code === parseInt(value)) ===
        undefined
    )
      return texts.job_application.error.invalid_country_code;
  },
  consent: (value) => {
    if (typeof value !== 'boolean') return;
    if (!value) return texts.job_application.error.consent_required;
  },
});

interface ApplicationFormProps {
  onSubmit: (
    formState: ApplicationFormSchema,
    formActions: UseFormActions
  ) => void;
  loading?: boolean;
  submitFailed?: number;
  submitSucceeded?: number;
  resumeSubmitError?: string;
  onResumeChange?: () => void;
}

export function ApplicationForm({
  loading,
  onSubmit,
  submitFailed,
  submitSucceeded,
  resumeSubmitError,
  onResumeChange,
}: ApplicationFormProps) {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const { texts } = useTranslation();
  const validator = useMemo(() => getValidator(texts), [texts]);

  const { handleSubmit, getFieldProps, setFieldValue, values, errors } =
    useForm<ApplicationFormSchema>({
      initialValues: {
        fullName: '',
        email: '',
        countryCode: '',
        phoneNumber: '',
        linkedInProfile: '',
        consent: false,
        information: '',
        resume: undefined,
      },
      validator,
      validateOnBlur: true,
      validateOnChange: true,
    });

  useEffect(() => {
    if (!submitSucceeded) return;

    setShowSuccessMessage(true);
    const timer = setTimeout(() => {
      setShowSuccessMessage(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [submitSucceeded]);

  useEffect(() => {
    if (!submitFailed) return;

    setShowErrorMessage(true);
    const timer = setTimeout(() => {
      setShowErrorMessage(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [submitFailed]);

  const resumeFieldProps = getFieldProps('resume');
  const resumeError = resumeSubmitError || errors.resume;
  const shouldDisableButton = loading || Boolean(resumeError);

  const renderSubmitLabel = () => {
    if (loading) return <SvgIcon src={iconSpinnerSrc} className='h-6 w-6' />;
    return texts.job_application.submit;
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='flex flex-col gap-1 md:gap-2'
      data-tracker-component='form'
    >
      <div className='flex flex-col md:flex-row gap-2 md:gap-6'>
        <Input
          placeholder={texts.job_application.full_name}
          label={texts.job_application.full_name}
          isRequired
          {...getFieldProps('fullName')}
          classNames={{
            root: 'w-full',
            input: { root: inputBorderClasses },
          }}
        />
        <Input
          placeholder={texts.job_application.email}
          label={texts.job_application.email}
          isRequired
          {...getFieldProps('email')}
          classNames={{
            root: 'w-full',
            input: { root: inputBorderClasses },
          }}
        />
      </div>
      <div className='flex flex-col md:flex-row gap-2 md:gap-6'>
        <div className='w-full'>
          <TelephoneInput
            countryCodeProps={{
              ...getFieldProps('countryCode'),
              onOptionChange: (value) => setFieldValue('countryCode', value),
              label: texts.job_application.phone_number,
              placeholder: texts.job_application.country_code_placeholder,
              classNames: {
                inputField: {
                  root: cn(inputBorderClasses, '[&_span]:text-gray-950'),
                },
              },
              popoverProps: {
                targetProps: {
                  'data-tracker-context': 'country_code',
                },
              },
            }}
            phoneNumberProps={{
              ...getFieldProps('phoneNumber'),
              classNames: {
                label: { root: 'text-gray-950' },
                input: { root: inputBorderClasses },
              },
            }}
          />
        </div>
        <div className='w-full'>
          <Input
            leftIcon={<SvgIcon src={iconProfileLinkSrc} className='h-4 w-4' />}
            placeholder={texts.job_application.link_to_your_profile}
            label={texts.job_application.link_to_your_profile}
            {...getFieldProps('linkedInProfile')}
            classNames={{
              input: { root: inputBorderClasses },
            }}
          />
        </div>
      </div>
      <FileUploadField
        label={`${texts.job_application.resume_cv_label}*`}
        inputLabels={{
          label: texts.job_application.input_label,
          disclaimer: getIntlText(texts.job_application.resume_disclaimer, {
            maxFileSize: String(MAX_RESUME_FILE_SIZE_MB),
          }),
        }}
        name={resumeFieldProps.name}
        onBlur={resumeFieldProps.onBlur}
        error={resumeError}
        value={values.resume ? [values.resume] : []}
        acceptedFileTypes={SUPPORTED_FILE_TYPES}
        onChange={(files) => {
          onResumeChange?.();
          setFieldValue('resume', files[0]);
        }}
        onFileRemove={() => {
          onResumeChange?.();
          setFieldValue('resume', undefined);
        }}
      />
      <div className='pb-5 md:pb-8'>
        <Text variant='body-m' classNames={{ wrapper: 'pb-2' }}>
          {texts.job_application.add_cover_letter_or_docs}
        </Text>

        <Input
          placeholder={texts.job_application.type_here}
          as='textarea'
          {...getFieldProps('information')}
          maxLength={COVER_LETTER_MAX_LENGTH}
          classNames={{ input: { root: inputBorderClasses } }}
        />
      </div>
      <div className='pb-8'>
        <Checkbox
          {...getFieldProps('consent')}
          value='consent'
          checked={values.consent}
          classNames={{
            label: 'font-mono',
          }}
          label={
            <span>
              {texts.job_application.privacy_notice_confirm_text}
              <LinkButton
                variant='text'
                href={DATA_PROTECTION_URL}
                openInNewTab
                slim
                classNames={{ root: 'text-body-l pl-1 underline' }}
              >
                {texts.job_application.privacy_notice_link_text}
              </LinkButton>
            </span>
          }
        />
      </div>

      <Text variant='body-m' classNames={{ wrapper: 'text-gray-500' }}>
        {getIntlText(texts.job_application.commitment_to_diversity, {
          title: siteConfig.title,
        })}
      </Text>
      <div className='mt-5 md:mt-4 flex flex-col gap-2'>
        {showSuccessMessage && (
          <Text
            variant='body-s'
            role='status'
            aria-live='polite'
            classNames={{
              wrapper: 'flex items-center gap-2 text-success-500 font-mono',
            }}
          >
            <SvgIcon src={iconCheckSrc} className='h-4 w-4 shrink-0' />
            {texts.job_application.application_submitted_successfully}
          </Text>
        )}
        {showErrorMessage && (
          <Text
            variant='body-s'
            role='alert'
            classNames={{ wrapper: 'text-error-500 font-mono' }}
          >
            {texts.job_application.failed_to_submit}
          </Text>
        )}
        <Button
          type='submit'
          fullwidth
          classNames={{
            inner: loading ? 'animate-[spin_2s_linear_infinite]' : '',
          }}
          disabled={shouldDisableButton}
        >
          {renderSubmitLabel()}
        </Button>
      </div>
    </form>
  );
}
